import Button from "../../components/Button/Button";
import NavBar from "../../components/NavBar/NavBar";
import "./HomePage.scss";
import heroFrcophthTopics from "../../assets/oq/hero-frcophth-topics.png";
import questionsPage from "../../assets/oq/questionsPage.png";
import modulesPage from "../../assets/oq/modulesPage.png";
import { Button as BootstrapButton } from "react-bootstrap";

import dashboardPage from "../../assets/oq/Dashboard.png";
import Checkmark from "../../components/Checkmark/Checkmark";
import Footer from "../../components/Footer/Footer";
import { useLocation, useNavigate } from "react-router";
import { useEffect } from "react";
import products from "../SignUpPage/products";
import { FaExternalLinkAlt } from "react-icons/fa";

const OqHomePage = () => {
  const { hash } = useLocation();
  const navigate = useNavigate();

  const newsletterFormSubmitted = (event: React.FormEvent) => {
    event.preventDefault();
    console.log(event);
  };

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div id="OqHomePage">
      <NavBar />

      <section className="home-hero" aria-label="Introduction">
        <div className="site-container home-hero__inner">
          <div className="home-hero__copy">
            <p className="home-eyebrow">FRCOphth · MSRA · Interviews</p>
            <h1 className="home-hero__title">Master the FRCOphth exams</h1>
            <p className="home-hero__lead">
              Prepare for <strong>FRCOphth Parts 1 &amp; 2</strong>, the <strong>MSRA</strong>, and{" "}
              <strong>ST1 ophthalmology interviews</strong> with <strong>exam-style questions</strong> that mirror
              real papers in <strong>wording and format</strong>, <strong>timed mock exams</strong> when you are ready,
              and explanations grounded in UK practice. <strong>Co-built by ophthalmologists, for ophthalmologists.</strong>
            </p>
            <div className="home-hero__actions">
              <div className="home-hero__signup">
                <Button text="Sign up" linkTo="/sign-up" />
              </div>
            </div>
            <p className="home-hero__trust">
              Exam-realistic format · UK-aligned explanations · Built with practising ophthalmologists
            </p>
          </div>
          <div className="home-hero__visual">
            <img
              className="home-hero__image"
              src={heroFrcophthTopics}
              alt="FRCOphth Part 1 Topics dashboard with modules, progress, and practice options"
              width={1024}
              height={732}
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <div id="other-sections" className="home-body">
        <section id="about-oq" className="home-section home-section--alt">
          <div className="site-container">
            <div className="home-split">
              <div className="home-split__text">
                <p className="home-section__label">The platform</p>
                <h2 className="home-section__title">About OQ</h2>
                <p className="home-section__intro">
                  Know what the papers feel like before you sit them. Our <strong>FRCOphth Part 1 and Part 2</strong>{" "}
                  question banks test <strong>every module and every area</strong> within those modules—so gaps do not
                  surprise you on the day.
                </p>
                <ul className="home-feature-list">
                  <li>
                    <Checkmark />
                    <span>
                      <strong>FRCOphth Part 1 &amp; Part 2</strong> banks: full syllabus depth—every module, every area.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Exam-style questions</strong> that match the wording and format of real exam questions.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Timed practice</strong> and full <strong>mock exams</strong> to rehearse under exam
                      conditions.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      Instant, detailed feedback with explanations aligned to UK guidelines—plus analytics on where you
                      stand.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="home-split__media">
                <div className="home-media-frame">
                  <img src={questionsPage} alt="Question practice screen" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="why-us" className="home-section">
          <div className="site-container">
            <div className="home-split home-split--reverse">
              <div className="home-split__text">
                <p className="home-section__label">Why specialists choose us</p>
                <h2 className="home-section__title">Purpose-built for ophthalmology</h2>
                <p className="home-section__intro">
                  OphthalmologyQ is <strong>co-built by ophthalmologists for ophthalmologists</strong>—a dedicated
                  platform, not a generic medical bank with an ophthalmology add-on.
                </p>
                <ul className="home-feature-list">
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Co-built by ophthalmologists for ophthalmologists</strong>—clinical rigour in every
                      question.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Interview coaching</strong> delivered by <strong>leading UK ophthalmologists</strong> who
                      know the ST1 process inside out.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      Thousands of questions across <strong>FRCOphth Parts 1 &amp; 2</strong>, <strong>MSRA</strong>,
                      interviews, and more—regularly updated.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Timed mock exams</strong> and flexible practice so you can build speed and accuracy on your
                      schedule.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="home-split__media">
                <div className="home-media-frame home-media-frame--tint2">
                  <img src={modulesPage} alt="Modules and learning paths" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="our-vision" className="home-section home-vision">
          <div className="site-container">
            <p className="home-section__label home-section__label--center">Coverage</p>
            <h2 className="home-section__title home-section__title--center">Our vision</h2>
            <p className="home-vision__lead">
              We want you to walk into the exam having already practised the <strong>full syllabus under exam
              conditions</strong>—with <strong>exam-style questions</strong>, <strong>timed mocks</strong>, and support
              from <strong>UK ophthalmology specialists</strong> at every stage of your preparation.
            </p>

            <div className="home-vision__chips">
              <div className="home-vision-chip">
                <Checkmark />
                <span>MSRA</span>
              </div>
              <div className="home-vision-chip">
                <Checkmark />
                <span>Duke Elder</span>
              </div>
              <div className="home-vision-chip">
                <Checkmark />
                <span>FRCOphth Part 1</span>
              </div>
              <div className="home-vision-chip">
                <Checkmark />
                <span>FRCOphth Part 2</span>
              </div>
            </div>

            <div className="home-dashboard-showcase">
              <img src={dashboardPage} alt="Dashboard showing your study progress" />
            </div>
          </div>
        </section>

        <section id="pricing" className="home-section home-pricing">
          <div className="site-container">
            <p className="home-section__label home-section__label--center">Question banks &amp; resources</p>
            <h2 className="home-section__title home-section__title--center">Choose your preparation</h2>
            <p className="home-pricing__intro">
              <strong>FRCOphth Part 1 &amp; Part 2</strong> banks deliver <strong>every module and every area</strong> in
              exam-realistic form; add <strong>MSRA</strong>, interview packs, or <strong>coaching from leading UK
              ophthalmologists</strong> to match how you learn. Pick a product for full detail and pricing.
            </p>

            <div className="oq-product-cards-wrapper">
              {products.map((product) => {
                const isPurchasable = !((product as { purchasable?: boolean }).purchasable === false);
                const externalPurchaseUrl = (product as { externalPurchaseUrl?: string }).externalPurchaseUrl;
                const showAmazonPurchase = product.id === "duke_elder" && externalPurchaseUrl;
                const button = (
                  <>
                    <BootstrapButton
                      className="home-product-btn home-product-btn--primary"
                      onClick={() => {
                        navigate("/products/" + product.id);
                      }}
                    >
                      Learn more
                    </BootstrapButton>
                    {showAmazonPurchase && (
                      <BootstrapButton
                        className="home-product-btn home-product-btn--external"
                        aria-label="Purchase on Amazon (opens in new tab)"
                        onClick={() => {
                          window.open(externalPurchaseUrl, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <span className="home-product-btn__label">
                          Purchase
                          <FaExternalLinkAlt className="home-product-btn__link-icon" aria-hidden />
                        </span>
                      </BootstrapButton>
                    )}
                    {isPurchasable && !product.comingSoon && !showAmazonPurchase && (
                      <BootstrapButton
                        className="home-product-btn home-product-btn--secondary"
                        onClick={() => {
                          const checkoutUrl = (product as { checkoutUrl?: string }).checkoutUrl;
                          if (checkoutUrl) {
                            window.open(checkoutUrl, "_blank", "noopener,noreferrer");
                          } else {
                            navigate("/sign-up?product=" + product.id);
                          }
                        }}
                      >
                        Purchase
                      </BootstrapButton>
                    )}
                  </>
                );
                return (
                  <div className={`oq-product-card ${product.css}`} key={product.id}>
                    <h5>{product.title}</h5>
                    <p>{product.description}</p>
                    <p className="oq-product-card-pricing">
                      {product.price ? (
                        <>
                          <span>£{product.price}</span> / {product.length}
                          {product.comingSoon && (
                            <span
                              className={
                                product.id === "portfolio_builder"
                                  ? "coming-soon-pill coming-soon-pill--pink"
                                  : "coming-soon-pill"
                              }
                            >
                              coming soon
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span
                            style={{
                              visibility: "hidden",
                            }}
                          >
                            £
                          </span>
                        </>
                      )}
                    </p>
                    <div className="home-product-card__actions">{button}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="newsletter" className="home-section home-newsletter">
          <div className="site-container">
            <p className="home-newsletter__label">Stay informed</p>
            <h2 className="home-section__title home-section__title--center">Newsletter</h2>
            <p className="home-newsletter__text">
              Occasional updates on new questions, syllabus changes, and study strategy—no clutter.
            </p>

            <form className="home-newsletter__form" onSubmit={newsletterFormSubmitted}>
              <input type="email" name="email" placeholder="you@hospital.nhs.uk" autoComplete="email" />
              <div className="home-newsletter__submit">
                <Button text="Subscribe" submit />
              </div>
            </form>
          </div>
        </section>
        <Footer />
      </div>
    </div>
  );
};

export default OqHomePage;
